/**
 * Tool parser for the Now Assist Skill Plugin.
 * Extracts tool definitions from Fluent DSL arrow functions,
 * validates them, and orchestrates record creation for all tool types.
 *
 * This module contains:
 * - extractToolDefinitions: Two-pass parser for arrow function → ToolDefinition[]
 * - parseToolConfig: Per-tool-type config extraction (giant switch)
 * - processTools: Main orchestrator that creates records for all tools
 * - Validation: name uniqueness, dependency cycles, ancestor/descendant hints
 * - Helper extractors: dependencies, inputs, outputs, targets, branches
 */

import { TOOL_TYPES, RESOURCE_MAPPING_DEFAULTS, RESOURCE_MAPPING_APPLICABILITY_SCRIPT } from './defaults'
import {
    type Context,
    type ObjectShape,
    type Record as BuildRecord,
    type Shape,
    CallExpressionShape,
    ElementAccessExpressionShape,
    IdentifierShape,
    PropertyAccessShape,
    VariableStatementShape,
} from '@servicenow/sdk-build-core'
import { ArrowFunctionShape, NowIdShape, NowIncludeShape } from '@servicenow/sdk-build-plugins'
import {
    getArrowExpressionBodyText,
    extractPropertyNameFromArrowBody,
    extractBranchesFromArrowBody,
} from './arrow-function-helpers'
import { applyDefaults } from './skill-plugin-helpers'
import type { ToolDefinition, ToolValidationResult } from './tool-types'
import {
    SYS_ONE_EXTEND_RESOURCE_MAPPING,
    SYS_ONE_EXTEND_RESOURCE_EDGE,
    normalizeInputFieldName,
    resolveTemplateValue,
    topologicalSortTools,
} from './tool-utils'

// Import individual tool processors
import { processInlineScriptTool } from './tools/inline-script-tool'
import { processScriptTool } from './tools/script-tool'
import { processSubflowTool } from './tools/subflow-tool'
import { processWebSearchTool } from './tools/web-search-tool'
import { processFlowActionTool } from './tools/flow-action-tool'
import { processSkillTool } from './tools/skill-tool'
import { processDecisionTool, createDecisionEdges } from './tools/decision-tool'

/**
 * Intermediate tool data collected in first pass (before dependency resolution)
 */
interface ToolParseData {
    methodName: string
    toolName: string
    variableName: string | undefined
    config: ObjectShape
}

/**
 * Extract tool definitions from an arrow function shape
 * Parses the function body and extracts tool builder calls (t.InlineScript, t.Script, etc.)
 *
 * Uses a TWO-PASS approach to handle forward references in dependencies:
 * 1. First pass: Extract all tools and build the complete varName→toolName map
 * 2. Second pass: Parse tool configs with the complete map for dependency resolution
 *
 * This ensures that `depends: toolC` works even if toolC is declared AFTER the current tool.
 */
function extractToolDefinitions(arrowFunc: ArrowFunctionShape, diagnostics: Context['diagnostics']): ToolDefinition[] {
    const toolParseData: ToolParseData[] = []
    const toolNameToVarName = new Map<string, string>() // Maps variable name to tool name for dependency resolution

    const statements = arrowFunc.getStatements()

    // =========================================================================
    // FIRST PASS: Collect all tools and build the complete varName→toolName map
    // =========================================================================
    for (const statement of statements) {
        let callExpression: CallExpressionShape | undefined
        let variableName: string | undefined

        // Handle both direct call expressions and variable declarations
        if (statement instanceof VariableStatementShape) {
            const initializer = statement.getInitializer()
            if (initializer instanceof CallExpressionShape) {
                callExpression = initializer
                variableName = statement.getVariableName()?.getName()
            }
        } else if (statement instanceof CallExpressionShape) {
            callExpression = statement
        }

        if (!callExpression) {
            continue
        }

        // Parse the callee (e.g., "t.InlineScript", "t.Script")
        const callee = callExpression.getCallee()
        const parts = callee.split('.')
        if (parts.length !== 2) {
            continue
        }

        const [builderParam, methodName] = parts
        if (!builderParam || !methodName) {
            continue
        }

        // Extract tool name (first argument)
        const nameArg = callExpression.getArgument(0)
        if (!nameArg) {
            diagnostics.warn(callExpression, `Tool ${methodName} call missing name argument`)
            continue
        }
        const toolName = nameArg.ifString()?.getValue()
        if (!toolName) {
            diagnostics.warn(callExpression, `Tool ${methodName} name must be a string literal`)
            continue
        }

        // Extract config (second argument)
        const configArg = callExpression.getArgument(1)
        if (!configArg || !configArg.isObject()) {
            diagnostics.warn(callExpression, `Tool ${methodName} call missing config argument`)
            continue
        }
        const config = configArg.asObject()

        // Store variable name → tool name mapping for dependency resolution
        // This MUST happen in first pass so ALL mappings are available in second pass
        if (variableName) {
            toolNameToVarName.set(variableName, toolName)
        }

        // Store data for second pass
        toolParseData.push({ methodName, toolName, variableName, config })
    }

    // =========================================================================
    // SECOND PASS: Parse tool configs with the COMPLETE varName→toolName map
    // Now forward references like `depends: internal3` will resolve correctly
    // =========================================================================
    const tools: ToolDefinition[] = []
    for (const { methodName, toolName, config } of toolParseData) {
        const tool = parseToolConfig(methodName, toolName, config, toolNameToVarName, diagnostics)
        if (tool) {
            tool._source = config
            tools.push(tool)
        }
    }

    return tools
}

/**
 * Parse tool configuration based on method name
 */
function parseToolConfig(
    methodName: string,
    toolName: string,
    config: ObjectShape,
    toolNameToVarName: Map<string, string>,
    diagnostics: Context['diagnostics']
): ToolDefinition | null {
    // Extract common properties
    // Extract $id from config (Now.ID['...'] or raw string) — used for resource mapping identity
    // $id is REQUIRED for all tools
    const $idShape = config.get('$id', false)
    let toolExplicitId: string
    if ($idShape instanceof NowIdShape) {
        toolExplicitId = String($idShape.getValue())
    } else if ($idShape?.ifString()) {
        toolExplicitId = $idShape.ifString()!.getValue()
    } else {
        diagnostics.error(config, 'Tool config is missing required $id property')
        return null
    }
    // Extract $capabilityId from config (Now.ID['...'] or raw string) — used by Script/FlowAction/Subflow
    const $capabilityIdShape = config.get('$capabilityId', false)
    let toolCapabilityId: string | undefined
    if ($capabilityIdShape instanceof NowIdShape) {
        toolCapabilityId = String($capabilityIdShape.getValue())
    } else if ($capabilityIdShape?.ifString()) {
        toolCapabilityId = $capabilityIdShape.ifString()?.getValue()
    }
    const truncate = config.get('truncate')?.ifBoolean()?.getValue() ?? false
    // Pass resolve=false to prevent infinite recursion on self-referencing depends
    const depends = extractDependencies(config.get('depends', false), toolNameToVarName)
    const conditionShape = config.get('condition')?.ifObject()
    let conditionScript: string | undefined
    if (conditionShape) {
        const scriptShape = conditionShape.get('script')
        if (scriptShape?.is(NowIncludeShape)) {
            // Now.include('./condition.server.js') — get the included file content
            conditionScript = (scriptShape as NowIncludeShape).getValue()
        } else if (scriptShape?.ifString()?.getValue()) {
            conditionScript = scriptShape.ifString()!.getValue()
        } else if (scriptShape instanceof ArrowFunctionShape) {
            const returnVal = scriptShape.getReturnValue()
            const stmts = scriptShape.getStatements()
            if (returnVal) {
                conditionScript = `return ${returnVal.getCode()};`
            } else if (stmts.length > 0) {
                conditionScript = stmts.map((s: Shape) => s.getCode()).join('\n')
            }
        }
    }
    const condition: { type?: string; script?: string } | undefined = conditionShape
        ? {
              ...(conditionShape.get('type')?.ifString()?.getValue()
                  ? { type: conditionShape.get('type')!.ifString()!.getValue() }
                  : {}),
              ...(conditionScript ? { script: conditionScript } : {}),
          }
        : undefined

    switch (methodName) {
        case 'InlineScript': {
            const scriptShape = config.get('script')
            let script: string | undefined
            if (scriptShape?.is(NowIncludeShape)) {
                // Now.include('./x.server.js') — get the included file content
                script = (scriptShape as NowIncludeShape).getValue()
            } else if (scriptShape?.isString()) {
                script = scriptShape.asString().getValue()
            } else if (scriptShape instanceof ArrowFunctionShape) {
                // Arrow function: (context) => expr  OR  (context) => { ... }
                // Extract the body so it can be properly wrapped in the IIFE
                // biome-ignore lint/suspicious/noExplicitAny: getReturnValue exists at runtime but not in published types
                const returnVal = (scriptShape as any).getReturnValue?.()
                const stmts = scriptShape.getStatements()
                if (returnVal) {
                    // Expression body: (context) => expr → "return expr;"
                    script = `return ${returnVal.getCode()};`
                } else if (stmts.length > 0) {
                    // Block body: (context) => { stmts } → "stmts"
                    script = stmts.map((s: Shape) => s.getCode()).join('\n')
                } else {
                    script = ''
                }
            } else if (scriptShape) {
                // For other types, get the code representation
                script = scriptShape.getCode?.() || ''
            }
            return {
                type: TOOL_TYPES.INLINE_SCRIPT,
                name: toolName,
                $id: toolExplicitId,
                script,
                truncate,
                depends,
                condition,
            }
        }

        case 'Script': {
            // Get the resolved shape - this will resolve variable references
            const scriptIdShape = config.get('scriptId')
            let scriptId: string | undefined

            // Handle: string | ReturnType<typeof ScriptInclude> | DbRecord<'sys_script_include'>
            // ScriptInclude returns an object with $id property
            if (scriptIdShape?.ifRecord()) {
                // DbRecord - extract the record's ID
                scriptId = scriptIdShape.ifRecord()?.getId().getValue()
            } else if (scriptIdShape?.isObject()) {
                // ScriptInclude object - extract $id from the object
                const $idShape = scriptIdShape.asObject().get('$id')
                if ($idShape instanceof NowIdShape) {
                    scriptId = String($idShape.getValue())
                } else if ($idShape?.ifRecord()) {
                    scriptId = $idShape.ifRecord()?.getId().getValue()
                } else if ($idShape) {
                    scriptId = String($idShape.getValue())
                }
            } else if (scriptIdShape?.isString()) {
                // Raw sys_id string
                scriptId = scriptIdShape.asString().getValue()
            } else if (scriptIdShape) {
                // Fallback: try to get value as string
                scriptId = String(scriptIdShape.getValue())
            }

            const scriptFunctionName = config.get('scriptFunctionName')?.ifString()?.getValue()
            const inputs = extractInputs(config.get('inputs'), toolNameToVarName)

            // Extract output object from config: { $id, name?, dataType? }
            let output: { $id: string; name?: string; dataType?: string } | undefined
            const outputShape = config.get('output', false)
            if (outputShape?.isObject()) {
                const outputObj = outputShape.asObject()
                // $id is mandatory in type definitions - TypeScript enforces this at compile time
                const $outputId = String(outputObj.get('$id').getValue())
                const outputName = outputObj.get('name', false)?.ifString()?.getValue()
                const outputType = outputObj.get('type', false)?.ifString()?.getValue()
                output = {
                    $id: $outputId,
                    ...(outputName ? { name: outputName } : {}),
                    ...(outputType ? { type: outputType } : {}),
                }
            }

            return {
                type: TOOL_TYPES.SCRIPT,
                name: toolName,
                $id: toolExplicitId,
                $capabilityId: toolCapabilityId,
                output,
                scriptId,
                scriptFunctionName,
                inputs,
                truncate,
                depends,
                condition,
            }
        }

        case 'FlowAction': {
            const inputs = extractFlowActionInputs(config.get('inputs'), toolNameToVarName)
            const outputs = extractFlowActionOutputs(config.get('outputs'))

            // Handle: string | ActionRef (ActionDefinition return) | Now.ID reference
            const actionIdShape = config.get('actionId', false)
            let actionId: string | undefined

            if (actionIdShape instanceof NowIdShape) {
                // User passed Now.ID['action_name']
                const nowIdValue = actionIdShape.getValue()
                if (typeof nowIdValue === 'string') {
                    actionId = nowIdValue
                }
            } else if (actionIdShape instanceof IdentifierShape) {
                // User passed a variable reference (e.g., myAction from import)
                const resolvedShape = actionIdShape.resolve(true)
                const resolvedRecord = resolvedShape?.ifRecord()
                if (resolvedRecord) {
                    const recordId = resolvedRecord.getId()
                    if (recordId instanceof NowIdShape) {
                        const idValue = recordId.getValue()
                        if (typeof idValue === 'string') {
                            actionId = idValue
                        }
                    } else {
                        const idValue = recordId.getValue()
                        if (typeof idValue === 'string') {
                            actionId = idValue
                        }
                    }
                }
            } else if (actionIdShape?.ifRecord()) {
                // Direct record reference
                const recordId = actionIdShape.ifRecord()?.getId()
                if (recordId instanceof NowIdShape) {
                    const idValue = recordId.getValue()
                    if (typeof idValue === 'string') {
                        actionId = idValue
                    }
                } else if (recordId) {
                    const idValue = recordId.getValue()
                    if (typeof idValue === 'string') {
                        actionId = idValue
                    }
                }
            } else if (actionIdShape?.ifString()) {
                // Raw sys_id string
                actionId = actionIdShape.ifString()?.getValue()
            }

            return {
                type: TOOL_TYPES.FLOW_ACTION,
                name: toolName,
                $id: toolExplicitId,
                $capabilityId: toolCapabilityId,
                actionId,
                inputs,
                outputs,
                depends,
                condition,
            }
        }

        case 'Subflow': {
            const inputs = extractInputs(config.get('inputs'), toolNameToVarName)
            // Extract dynamic outputs (like FlowAction)
            const outputs = extractFlowActionOutputs(config.get('outputs'))

            // Get the raw shape without resolving to detect different reference types
            const subflowIdShape = config.get('subflowId', false)
            let subflowId: string | undefined
            let subflowIdIsReference = false
            let subflowIdExplicitId: string | undefined

            // Handle: string | SubflowRef (Subflow definition) | Now.ID reference
            if (subflowIdShape instanceof NowIdShape) {
                // User passed Now.ID['subflow_name']
                const nowIdValue = subflowIdShape.getValue()
                if (typeof nowIdValue === 'string') {
                    subflowIdExplicitId = nowIdValue
                    subflowIdIsReference = true
                }
            } else if (subflowIdShape instanceof IdentifierShape) {
                // User passed a variable reference (e.g., documentSummarizerSubflow from import)
                const resolvedShape = subflowIdShape.resolve(true)
                const resolvedRecord = resolvedShape?.ifRecord()
                if (resolvedRecord) {
                    // The record's id contains the subflow sys_id or explicitId
                    const recordId = resolvedRecord.getId()
                    if (recordId instanceof NowIdShape) {
                        const idValue = recordId.getValue()
                        if (typeof idValue === 'string') {
                            subflowIdExplicitId = idValue
                            subflowIdIsReference = true
                        }
                    } else {
                        const idValue = recordId.getValue()
                        if (typeof idValue === 'string') {
                            subflowId = idValue
                        }
                    }
                }
            } else if (subflowIdShape?.ifRecord()) {
                // Direct record reference
                const recordId = subflowIdShape.ifRecord()?.getId()
                if (recordId instanceof NowIdShape) {
                    const idValue = recordId.getValue()
                    if (typeof idValue === 'string') {
                        subflowIdExplicitId = idValue
                        subflowIdIsReference = true
                    }
                } else if (recordId) {
                    const idValue = recordId.getValue()
                    if (typeof idValue === 'string') {
                        subflowId = idValue
                    }
                }
            } else if (subflowIdShape?.ifString()) {
                // Raw sys_id string
                subflowId = subflowIdShape.ifString()?.getValue()
            }

            return {
                type: TOOL_TYPES.SUBFLOW,
                name: toolName,
                $id: toolExplicitId,
                $capabilityId: toolCapabilityId,
                subflowId,
                subflowIdIsReference,
                subflowIdExplicitId,
                inputs,
                outputs: outputs && outputs.length > 0 ? outputs : undefined,
                truncate,
                depends,
                condition,
            }
        }

        case 'Skill': {
            // Parse per-output truncate config (FixedOutputTruncateConfig)
            // truncate is an object with per-output boolean flags: { provider, response, error, errorcode, status }
            const truncateShape = config.get('truncate')?.ifObject()
            const truncateConfig: Record<string, boolean> | undefined = truncateShape
                ? {
                      provider: truncateShape.get('provider')?.ifBoolean()?.getValue() ?? false,
                      response: truncateShape.get('response')?.ifBoolean()?.getValue() ?? false,
                      error: truncateShape.get('error')?.ifBoolean()?.getValue() ?? false,
                      errorcode: truncateShape.get('errorcode')?.ifBoolean()?.getValue() ?? false,
                      status: truncateShape.get('status')?.ifBoolean()?.getValue() ?? false,
                  }
                : undefined

            // Get the raw shape without resolving to detect NowIdShape or NowAssistSkillConfig reference
            const skillIdShape = config.get('skillId', false)
            let skillId: string | undefined
            let skillIdIsReference = false
            let skillIdExplicitId: string | undefined

            // Handle: string | ReturnType<typeof NowAssistSkillConfig> | DbRecord<'sys_one_extend_capability'>
            if (skillIdShape instanceof NowIdShape) {
                // User passed Now.ID['skill_name'] - derive capability explicitId
                // The capability record uses explicitId = "${skillId}__capability"
                const baseId = skillIdShape.getValue()
                skillIdExplicitId = `${baseId}__capability`
                skillIdIsReference = true
            } else if (skillIdShape instanceof IdentifierShape) {
                // User passed a variable reference (e.g., helperSkill from import)
                // The IdentifierShape resolves to a Record or NowAssistSkillConfig tuple
                const resolvedShape = skillIdShape.resolve(true)
                const resolvedRecord = resolvedShape?.ifRecord()
                if (resolvedRecord) {
                    // Direct record reference - extract capability sys_id
                    const capabilitySysId = resolvedRecord.getId().getValue()
                    if (capabilitySysId) {
                        skillId = capabilitySysId
                    }
                } else if (resolvedShape?.isArray()) {
                    // NowAssistSkillConfig tuple: [definition, promptConfig]
                    const firstElement = resolvedShape.asArray().getElements()[0]
                    if (firstElement?.isObject()) {
                        const defObj = firstElement.asObject()
                        const $idShape = defObj.get('$id')
                        if ($idShape instanceof NowIdShape) {
                            const baseId = $idShape.getValue()
                            skillIdExplicitId = `${baseId}__capability`
                            skillIdIsReference = true
                        }
                    }
                }
            } else if (skillIdShape?.ifRecord()) {
                // NowAssistSkillConfig result or DbRecord<'sys_one_extend_capability'>
                const recordId = skillIdShape.ifRecord()?.getId().getValue()
                if (recordId) {
                    // Derive capability explicitId from the skill's $id
                    skillIdExplicitId = `${recordId}__capability`
                    skillIdIsReference = true
                }
            } else if (skillIdShape?.isArray()) {
                // Direct NowAssistSkillConfig tuple reference: [definition, promptConfig]
                const firstElement = skillIdShape.asArray().getElements()[0]
                if (firstElement?.isObject()) {
                    const defObj = firstElement.asObject()
                    const $idShape = defObj.get('$id')
                    if ($idShape instanceof NowIdShape) {
                        const baseId = $idShape.getValue()
                        skillIdExplicitId = `${baseId}__capability`
                        skillIdIsReference = true
                    }
                }
            } else if (skillIdShape?.isString()) {
                // Raw sys_id string - validate it's a proper GUID before using
                const rawSkillId = skillIdShape.asString().getValue()
                skillId = rawSkillId
            }

            // Extract inputs with definitionAttributeId (sys_id of target skill's definition attribute)
            const inputs = extractSkillInputs(config.get('inputs'), toolNameToVarName)
            // Extract unified outputs: 5 mandatory + custom, each with definitionAttributeId and truncate
            const skillOutputs = extractSkillOutputs(config.get('outputs'))
            return {
                type: TOOL_TYPES.SKILL,
                name: toolName,
                $id: toolExplicitId,
                skillId,
                skillIdIsReference,
                skillIdExplicitId,
                inputs,
                skillOutputs,
                depends,
                condition,
            }
        }

        case 'WebSearch': {
            const searchType = config.get('searchType')?.ifString()?.getValue() as
                | 'ai_answers'
                | 'searching_and_scraping'
                | undefined

            // query and sites can be string literals, template expressions, or ScriptTypeToolInput
            // Pass unresolved shapes (resolve=false) to preserve PropertyAccessShape for template resolution
            const queryShape = config.get('query', false)
            const queryScript = extractScriptTypeToolInput(queryShape)
            let query: string | undefined
            let queryScriptValue: string | undefined
            let queryValueType: 'template' | 'script' | undefined
            if (queryScript) {
                query = ''
                queryScriptValue = queryScript
                queryValueType = 'script'
            } else {
                query = resolveTemplateValue(queryShape, toolNameToVarName)
            }

            const sitesShape = config.get('sites', false)
            const sitesScript = extractScriptTypeToolInput(sitesShape)
            let sites: string | undefined
            let sitesScriptValue: string | undefined
            let sitesValueType: 'template' | 'script' | undefined
            if (sitesScript) {
                sites = ''
                sitesScriptValue = sitesScript
                sitesValueType = 'script'
            } else {
                sites = resolveTemplateValue(sitesShape, toolNameToVarName) || undefined
            }

            const aiSearchProviders = config.get('aiSearchProviders')?.ifString()?.getValue()
            const numResults = config.get('numResults')?.ifNumber()?.getValue()
            const country = config.get('country')?.ifString()?.getValue()
            const maxTokens = config.get('maxTokens')?.ifNumber()?.getValue()

            // Parse per-output truncate config (FixedOutputTruncateConfig)
            // truncate is an object with per-output boolean flags: { provider, response, error, errorcode, status }
            const truncateShape = config.get('truncate')?.ifObject()
            let truncateConfig: Record<string, boolean> | undefined
            if (truncateShape) {
                truncateConfig = {}
                const outputKeys = ['provider', 'response', 'error', 'errorcode', 'status'] as const
                for (const key of outputKeys) {
                    const val = truncateShape.get(key)?.ifBoolean()?.getValue()
                    if (val !== undefined) {
                        truncateConfig[key] = val
                    }
                }
            }

            return {
                type: TOOL_TYPES.WEB_SEARCH,
                name: toolName,
                $id: toolExplicitId,
                searchType,
                query,
                queryScriptValue,
                queryValueType,
                sites,
                sitesScriptValue,
                sitesValueType,
                aiSearchProviders,
                numResults,
                country,
                maxTokens,
                truncate: false, // WebSearch uses truncateConfig instead of simple boolean
                truncateConfig,
                depends,
                condition,
            }
        }

        case 'Decision': {
            const targets = extractTargets(config.get('targets'))
            const branches = extractBranches(config.get('branches'), toolNameToVarName)
            const defaultTarget = extractDefaultTarget(config.get('default'))
            return {
                type: TOOL_TYPES.DECISION,
                name: toolName,
                $id: toolExplicitId,
                targets,
                branches,
                default: defaultTarget,
                depends,
            }
        }

        default:
            diagnostics.warn(config, `Unknown tool type: ${methodName}`)
            return null
    }
}

/**
 * Extract a single dependency tool name from a shape.
 *
 * IMPORTANT: This function must avoid calling resolve() on self-referencing shapes.
 * A self-reference (e.g., `depends: internal2` inside `internal2`'s definition) causes
 * infinite recursion in the SDK's resolution mechanism, hanging the build.
 *
 * The guard in typescript.ts (getValueDeclaration) now returns undefined for self-references,
 * but we still avoid unnecessary resolve() calls here for safety.
 */
function extractSingleDependency(
    dependsShape: Shape | undefined,
    toolNameToVarName: Map<string, string>
): string | undefined {
    // Return undefined for missing property or UndefinedShape
    if (!dependsShape || dependsShape.isUndefined?.()) {
        return undefined
    }

    // Get the code of the unresolved element (variable name for IdentifierShape)
    const code = dependsShape.getCode?.()
    // Skip if code is literally 'undefined' - means no valid dependency
    if (code && code !== 'undefined') {
        // Check if it's a variable reference mapped to a tool name
        // This includes self-references - we just record the tool name and let
        // validateToolDefinitions catch the self-reference error later
        const toolName = toolNameToVarName.get(code)
        if (toolName) {
            return toolName
        } else {
            // Try resolving only if it's not a simple identifier (to avoid self-reference issues)
            // The typescript.ts guard should protect us, but we're extra careful here
            if (dependsShape.isResolvable?.() && !dependsShape.isIdentifier?.()) {
                const resolved = dependsShape.resolve?.(true)
                if (resolved instanceof CallExpressionShape) {
                    const nameArg = resolved.getArgument?.(0)
                    const name = nameArg?.ifString?.()?.getValue()
                    if (name) {
                        return name
                    }
                }
            }
            // Unknown reference - use the code as-is
            return code
        }
    }

    return undefined
}

/**
 * Extract dependencies from depends (array of tool handles)
 *
 * Handles both array and single-value depends for backward compatibility.
 */
function extractDependencies(
    dependsShape: Shape | undefined,
    toolNameToVarName: Map<string, string>
): string[] | undefined {
    // Return undefined for missing property or UndefinedShape
    // Root nodes (tools with no depends) should have depends = undefined (JS value)
    if (!dependsShape || dependsShape.isUndefined?.()) {
        return undefined
    }

    // Handle array of dependencies
    if (dependsShape.isArray?.()) {
        const deps: string[] = []
        // Use getElements(false) to get unresolved elements (identifiers, not their values)
        const elements = dependsShape.asArray().getElements(false)
        for (const element of elements) {
            const dep = extractSingleDependency(element, toolNameToVarName)
            if (dep) {
                deps.push(dep)
            }
        }
        return deps.length > 0 ? deps : undefined
    }

    // Handle single dependency (backward compatibility)
    const singleDep = extractSingleDependency(dependsShape, toolNameToVarName)
    return singleDep ? [singleDep] : undefined
}

/**
 * Extract the script content from a ScriptTypeToolInput shape.
 * Detects ObjectShape with { type: 'script', script: '...' | Now.include() }
 * Returns the script content string, or undefined if not a ScriptTypeToolInput.
 */
function extractScriptTypeToolInput(valueShape: Shape | undefined): string | undefined {
    if (!valueShape || !valueShape.isObject()) {
        return undefined
    }
    const valueObj = valueShape.asObject()
    const typeVal = valueObj.get('type')?.ifString()?.getValue()
    if (typeVal !== 'script') {
        return undefined
    }
    const scriptShape = valueObj.get('script')
    if (scriptShape?.is(NowIncludeShape)) {
        // Now.include('./file.js') — get the included file content
        return (scriptShape as NowIncludeShape).getValue()
    }
    if (scriptShape?.ifString()?.getValue()) {
        return scriptShape.ifString()!.getValue()
    }
    return undefined
}

/**
 * Extract inputs array (for Script, Subflow, Skill tools)
 */
function extractInputs(
    inputsShape: Shape | undefined,
    toolNameToVarName: Map<string, string>
):
    | Array<{
          $id?: string
          name: string
          type?: string
          value: string
          scriptValue?: string
          valueType?: 'template' | 'script'
      }>
    | undefined {
    if (!inputsShape || !inputsShape.isArray()) {
        return undefined
    }

    const inputs: Array<{
        $id?: string
        name: string
        type?: string
        value: string
        scriptValue?: string
        valueType?: 'template' | 'script'
    }> = []
    const elements = inputsShape.asArray().getElements()

    for (const element of elements) {
        if (!element.isObject()) {
            continue
        }
        const obj = element.asObject()
        const name = obj.get('name')?.ifString()?.getValue()
        const type = obj.get('type')?.ifString()?.getValue()
        const idShape = obj.get('$id')
        const $id = idShape instanceof NowIdShape ? (idShape.getValue() as string) : idShape?.ifString()?.getValue()

        // Check if value is a ScriptTypeToolInput: { type: 'script', script: '...' | Now.include() }
        const valueShape = obj.get('value', false)
        const scriptInput = extractScriptTypeToolInput(valueShape)
        if (name && scriptInput) {
            inputs.push({
                ...($id ? { $id } : {}),
                name,
                ...(type ? { type } : {}),
                value: '',
                scriptValue: scriptInput,
                valueType: 'script',
            })
        } else {
            const value = resolveTemplateValue(valueShape, toolNameToVarName)
            if (name) {
                inputs.push({ ...($id ? { $id } : {}), name, ...(type ? { type } : {}), value })
            }
        }
    }

    return inputs.length > 0 ? inputs : undefined
}

/**
 * Extract Skill tool inputs — reads `definitionAttributeId` instead of `name`.
 * The definitionAttributeId (sys_id of sys_one_extend_definition_attribute) is stored
 * in the `name` field of the ToolDefinition input for consistency with processSkillTool.
 */
function extractSkillInputs(
    inputsShape: Shape | undefined,
    toolNameToVarName: Map<string, string>
):
    | Array<{ $id?: string; name: string; value: string; scriptValue?: string; valueType?: 'template' | 'script' }>
    | undefined {
    if (!inputsShape || !inputsShape.isArray()) {
        return undefined
    }

    const inputs: Array<{
        $id?: string
        name: string
        value: string
        scriptValue?: string
        valueType?: 'template' | 'script'
    }> = []
    const elements = inputsShape.asArray().getElements()

    for (const element of elements) {
        if (!element.isObject()) {
            continue
        }
        const obj = element.asObject()
        // Read definitionAttributeId instead of name
        const definitionAttributeId = obj.get('definitionAttributeId')?.ifString()?.getValue()
        const idShape = obj.get('$id')
        const $id = idShape instanceof NowIdShape ? (idShape.getValue() as string) : idShape?.ifString()?.getValue()

        const valueShape = obj.get('value', false)
        const scriptInput = extractScriptTypeToolInput(valueShape)
        if (definitionAttributeId && scriptInput) {
            inputs.push({
                ...($id ? { $id } : {}),
                name: definitionAttributeId,
                value: '',
                scriptValue: scriptInput,
                valueType: 'script',
            })
        } else {
            const value = resolveTemplateValue(valueShape, toolNameToVarName)
            if (definitionAttributeId) {
                inputs.push({ ...($id ? { $id } : {}), name: definitionAttributeId, value })
            }
        }
    }

    return inputs.length > 0 ? inputs : undefined
}

/**
 * Extract Skill tool unified outputs — reads an object with keys mapping to
 * { definitionAttributeId, truncate?, $id? } entries.
 * Returns a Record suitable for ToolDefinition.skillOutputs.
 */
function extractSkillOutputs(
    outputsShape: Shape | undefined
): Record<string, { $id?: string; definitionAttributeId: string | Shape; truncate?: boolean }> | undefined {
    if (!outputsShape || !outputsShape.isObject()) {
        return undefined
    }

    const outputs: Record<string, { $id?: string; definitionAttributeId: string | Shape; truncate?: boolean }> = {}
    const obj = outputsShape.asObject()
    const props = obj.properties()

    for (const [key, entryShape] of Object.entries(props)) {
        if (!entryShape || !entryShape.isObject()) {
            continue
        }
        const entryObj = entryShape.asObject()
        const defAttrIdShape = entryObj.get('definitionAttributeId')
        const definitionAttributeId = defAttrIdShape?.ifString()?.getValue()
        if (!definitionAttributeId) {
            continue
        }
        const truncate = entryObj.get('truncate')?.ifBoolean()?.getValue() ?? false
        const idShape = entryObj.get('$id')
        const $id = idShape instanceof NowIdShape ? (idShape.getValue() as string) : idShape?.ifString()?.getValue()

        outputs[key] = {
            ...($id ? { $id } : {}),
            definitionAttributeId,
            truncate,
        }
    }

    return Object.keys(outputs).length > 0 ? outputs : undefined
}

/**
 * Extract FlowAction inputs (with type)
 */
function extractFlowActionInputs(
    inputsShape: Shape | undefined,
    toolNameToVarName: Map<string, string>
):
    | Array<{
          $id?: string
          name: string
          type: string
          value: string
          scriptValue?: string
          valueType?: 'template' | 'script'
      }>
    | undefined {
    if (!inputsShape || !inputsShape.isArray()) {
        return undefined
    }

    const inputs: Array<{
        $id?: string
        name: string
        type: string
        value: string
        scriptValue?: string
        valueType?: 'template' | 'script'
    }> = []
    const elements = inputsShape.asArray().getElements()

    for (const element of elements) {
        if (!element.isObject()) {
            continue
        }
        const obj = element.asObject()
        const name = obj.get('name')?.ifString()?.getValue()
        const type = obj.get('type')?.ifString()?.getValue() || 'string'
        const idShape = obj.get('$id')
        const $id = idShape instanceof NowIdShape ? (idShape.getValue() as string) : idShape?.ifString()?.getValue()

        // Check if value is a ScriptTypeToolInput: { type: 'script', script: '...' | Now.include() }
        const valueShape = obj.get('value', false)
        const scriptInput = extractScriptTypeToolInput(valueShape)
        if (name && scriptInput) {
            inputs.push({
                ...($id ? { $id } : {}),
                name,
                type,
                value: '',
                scriptValue: scriptInput,
                valueType: 'script',
            })
        } else {
            const value = resolveTemplateValue(valueShape, toolNameToVarName)
            if (name) {
                inputs.push({ ...($id ? { $id } : {}), name, type, value })
            }
        }
    }

    return inputs.length > 0 ? inputs : undefined
}

/**
 * Extract FlowAction outputs
 */
function extractFlowActionOutputs(
    outputsShape: Shape | undefined
): Array<{ $id?: string; name: string; type: string; truncate?: boolean }> | undefined {
    if (!outputsShape || !outputsShape.isArray()) {
        return undefined
    }

    const outputs: Array<{ $id?: string; name: string; type: string; truncate?: boolean }> = []
    const elements = outputsShape.asArray().getElements()

    for (const element of elements) {
        if (!element.isObject()) {
            continue
        }
        const obj = element.asObject()
        const name = obj.get('name')?.ifString()?.getValue()
        const type = obj.get('type')?.ifString()?.getValue() || 'string'
        const truncate = obj.get('truncate')?.ifBoolean()?.getValue()
        const outIdShape = obj.get('$id')
        const $id =
            outIdShape instanceof NowIdShape ? (outIdShape.getValue() as string) : outIdShape?.ifString()?.getValue()
        if (name) {
            outputs.push({ ...($id ? { $id } : {}), name, type, ...(truncate !== undefined ? { truncate } : {}) })
        }
    }

    return outputs.length > 0 ? outputs : undefined
}

/**
 * Extract targets array for Decision node
 */
function extractTargets(targetsShape: Shape | undefined): readonly string[] | undefined {
    if (!targetsShape || !targetsShape.isArray()) {
        return undefined
    }

    const targets: string[] = []
    const elements = targetsShape.asArray().getElements()

    for (const element of elements) {
        const value = element.ifString()?.getValue()
        if (value) {
            targets.push(value)
        }
    }

    return targets.length > 0 ? targets : undefined
}

/**
 * Extract branches for Decision node.
 *
 * The branches config is an arrow function: `(targets) => [{ name, to, condition? }, ...]`
 * The ArrowFunctionShape's first statement is the returned ArrayShape containing
 * ObjectShape elements for each branch. Each branch has:
 *   - name: string (branch display name)
 *   - to: targets.ToolName (PropertyAccessShape) or string literal
 *   - condition: optional object with field/operator/value, script, or expression
 *
 * Field in condition can be:
 *   - t.input.fieldName (PropertyAccessShape) → {{fieldName}}
 *   - t.input['Field Name'] (ElementAccessExpressionShape) → {{field_name}}
 *   - toolVar.output (PropertyAccessShape) → {{ToolName.output}}
 *   - string literal → used as-is
 */
function extractBranches(
    branchesShape: Shape | undefined,
    toolNameToVarName: Map<string, string>
): Array<{ name: string; target: string; condition: string; conditionType: 'filter' | 'script' }> | undefined {
    if (!branchesShape || !(branchesShape instanceof ArrowFunctionShape)) {
        return undefined
    }

    const statements = branchesShape.getStatements()
    // Support both block body (statements[0]) and expression body (returnValue)
    // biome-ignore lint/suspicious/noExplicitAny: getReturnValue exists at runtime but not in published types
    const arrayShape = statements[0] ?? (branchesShape as any).getReturnValue?.()
    if (!arrayShape || !arrayShape.isArray()) {
        // Fallback: parse expression-body arrow function via ts-morph AST
        return extractBranchesFromArrowBody(branchesShape, toolNameToVarName)
    }

    const branches: Array<{ name: string; target: string; condition: string; conditionType: 'filter' | 'script' }> = []
    const elements = arrayShape.asArray().getElements()

    for (const element of elements) {
        if (!element.isObject()) {
            continue
        }
        const obj = element.asObject()

        // Extract branch name
        const branchName = obj.get('name')?.ifString()?.getValue() ?? ''

        // Extract target from 'to' property (preferred) or 'target' property (alias)
        // 'to'/'target' is typically targets.ToolName (PropertyAccessShape) or a string literal
        let target: string | undefined
        const toShape = obj.get('to', false)
        const targetShape = toShape?.isDefined() ? toShape : obj.get('target', false)
        if (targetShape instanceof PropertyAccessShape) {
            const parts = targetShape.getElements()
            // targets.ToolName -> last element is the target name
            if (parts.length >= 2) {
                target = parts[parts.length - 1]?.getName()
            }
        } else if (targetShape?.isString()) {
            target = targetShape.asString().getValue()
        }

        if (!target) {
            continue
        }

        // Extract condition (optional)
        // Supports: string condition, or object { field, operator, value } / { script } / { expression }
        let conditionStr = ''
        let conditionType: 'filter' | 'script' = 'filter'
        const rawConditionShape = obj.get('condition')
        const conditionAsString = rawConditionShape?.ifString()?.getValue()
        if (conditionAsString) {
            conditionStr = conditionAsString
        }
        const conditionShape = rawConditionShape?.ifObject()
        if (!conditionStr && conditionShape) {
            // Script-based condition: { script: '...' }
            const scriptVal = conditionShape.get('script')?.ifString()?.getValue()
            if (scriptVal) {
                conditionStr = scriptVal
                conditionType = 'script'
            } else {
                // Expression-based condition: { expression: '...' }
                const exprVal = conditionShape.get('expression')?.ifString()?.getValue()
                if (exprVal) {
                    conditionStr = exprVal
                } else {
                    // Field-based condition: { field, operator, value }
                    // Use resolveTemplateValue - same as other tools
                    // It handles: t.input.field → {{field}}, toolVar.output → {{tool.output}}, string → as-is
                    const fieldShape = conditionShape.get('field', false)
                    const field = resolveTemplateValue(fieldShape, toolNameToVarName)
                    const operator = conditionShape.get('operator')?.ifString()?.getValue()
                    const valueShape = conditionShape.get('value')
                    const value =
                        valueShape?.ifString()?.getValue() ?? valueShape?.ifNumber()?.getValue()?.toString() ?? ''
                    if (field && operator) {
                        const platformOperator = operator === 'is' ? '=' : operator === 'is not' ? '!=' : operator
                        conditionStr = `${field}${platformOperator}${value}`
                    }
                }
            }
        }

        branches.push({ name: branchName, target, condition: conditionStr, conditionType })
    }

    return branches.length > 0 ? branches : undefined
}
/**
 * Extract default target for Decision node.
 *
 * The default config is an arrow function: `(targets) => targets.ToolName`
 * The ArrowFunctionShape's first statement is the expression body, which is
 * a PropertyAccessShape like `targets.GeneralFix`. The last element name is
 * the target tool name.
 */
function extractDefaultTarget(defaultShape: Shape | undefined): string | undefined {
    if (!defaultShape || !(defaultShape instanceof ArrowFunctionShape)) {
        return undefined
    }

    const statements = defaultShape.getStatements()
    // Support both block body (statements[0]) and expression body (returnValue)
    // biome-ignore lint/suspicious/noExplicitAny: getReturnValue exists at runtime but not in published types
    const expr = statements[0] ?? (defaultShape as any).getReturnValue?.()

    // Expression body: (targets) => targets.ToolName
    if (expr instanceof PropertyAccessShape) {
        const parts = expr.getElements()
        // targets.ToolName -> last element is the target name
        if (parts.length >= 2) {
            return parts[parts.length - 1]?.getName()
        }
    }

    // Fallback: string literal
    if (expr?.isString()) {
        return expr.asString().getValue()
    }

    // Fallback for expression-body arrow functions where getStatements() is empty
    const propName = extractPropertyNameFromArrowBody(defaultShape)
    if (propName) {
        return propName
    }

    return undefined
}

/**
 * Validates tool definitions for a skill
 *
 * Checks for:
 * 1. Unique tool names within the skill
 * 2. Tool names don't collide with skill input names
 * 3. No self-reference in depends (tool cannot depend on itself)
 * 4. Dependencies must reference existing tools
 *
 * @param toolDefinitions - Array of tool definitions to validate
 * @param inputNames - Set of skill input names for collision detection
 * @returns Validation result with error message if invalid
 */
function validateToolDefinitions(toolDefinitions: ToolDefinition[], inputNames: Set<string>): ToolValidationResult {
    const toolNames = new Set<string>()

    // First pass: Build tool names set and validate uniqueness + input collision
    for (const tool of toolDefinitions) {
        if (toolNames.has(tool.name)) {
            return {
                isValid: false,
                errorMessage: `Duplicate tool name '${tool.name}'. Each tool must have a unique name within the skill.`,
            }
        }
        if (inputNames.has(tool.name)) {
            return {
                isValid: false,
                errorMessage: `Tool name '${tool.name}' conflicts with a skill input name. Tool names and input names must be unique.`,
            }
        }
        toolNames.add(tool.name)
    }

    // Second pass: Validate dependencies (requires complete toolNames set)
    for (const tool of toolDefinitions) {
        if (!tool.depends || tool.depends.length === 0) {
            continue
        }

        for (const depName of tool.depends) {
            if (depName === tool.name) {
                return {
                    isValid: false,
                    errorMessage: `Tool '${tool.name}' cannot depend on itself. Remove '${tool.name}' from its depends.`,
                }
            }
            if (!toolNames.has(depName)) {
                return {
                    isValid: false,
                    errorMessage: `Tool '${tool.name}' depends on '${depName}' which does not exist. Dependencies must reference tools defined in this skill.`,
                }
            }
        }
    }

    // Third pass: Cycle detection using DFS
    const cycle = detectDependencyCycle(toolDefinitions)
    if (cycle) {
        return {
            isValid: false,
            errorMessage: `Circular dependency detected: ${cycle.join(' → ')}. Tools cannot have circular dependencies.`,
        }
    }

    return { isValid: true }
}

/**
 * Detects circular dependencies in tool definitions using DFS
 *
 * @param toolDefinitions - Array of tool definitions to check
 * @returns Array representing the cycle path if found, null otherwise
 */
function detectDependencyCycle(toolDefinitions: ToolDefinition[]): string[] | null {
    const visited = new Set<string>()
    const recursionStack = new Set<string>()
    const path: string[] = []

    // Build dependency map for quick lookup
    const toolDepsMap = new Map<string, string[]>()
    for (const tool of toolDefinitions) {
        toolDepsMap.set(tool.name, tool.depends || [])
    }

    /**
     * DFS traversal to detect cycles
     * @returns true if cycle found
     */
    function dfs(toolName: string): boolean {
        visited.add(toolName)
        recursionStack.add(toolName)
        path.push(toolName)

        const deps = toolDepsMap.get(toolName) || []
        for (const dep of deps) {
            if (!visited.has(dep)) {
                if (dfs(dep)) {
                    return true
                }
            } else if (recursionStack.has(dep)) {
                // Found cycle - add the closing node to complete the cycle representation
                path.push(dep)
                return true
            }
        }

        path.pop()
        recursionStack.delete(toolName)
        return false
    }

    // Check each tool as a potential cycle start
    for (const tool of toolDefinitions) {
        if (!visited.has(tool.name)) {
            if (dfs(tool.name)) {
                // Extract just the cycle portion from the path
                const lastNode = path[path.length - 1]
                if (lastNode) {
                    const cycleStart = path.indexOf(lastNode)
                    return path.slice(cycleStart)
                }
                return path
            }
        }
    }

    return null
}

/**
 * Computes the set of ancestor tool names for a given tool using transitive closure of depends.
 * An ancestor is any tool that the given tool directly or transitively depends on.
 *
 * @param toolName - The tool to find ancestors for
 * @param toolDefinitions - All tool definitions
 * @returns Set of ancestor tool names
 */
function getAncestors(toolName: string, toolDefinitions: ToolDefinition[]): Set<string> {
    const ancestors = new Set<string>()
    const toolMap = new Map<string, ToolDefinition>()
    for (const tool of toolDefinitions) {
        toolMap.set(tool.name, tool)
    }

    function collectAncestors(name: string) {
        const tool = toolMap.get(name)
        if (!tool?.depends || tool.depends.length === 0) {
            return
        }

        for (const dep of tool.depends) {
            if (!ancestors.has(dep)) {
                ancestors.add(dep)
                collectAncestors(dep)
            }
        }
    }

    collectAncestors(toolName)
    return ancestors
}

/**
 * Computes the set of descendant tool names for a given tool.
 * A descendant is any tool that has the given tool in its ancestor chain.
 *
 * @param toolName - The tool to find descendants for
 * @param toolDefinitions - All tool definitions
 * @returns Set of descendant tool names
 */
function getDescendants(toolName: string, toolDefinitions: ToolDefinition[]): Set<string> {
    const descendants = new Set<string>()

    for (const tool of toolDefinitions) {
        if (tool.name === toolName) {
            continue
        }
        const ancestors = getAncestors(tool.name, toolDefinitions)
        if (ancestors.has(toolName)) {
            descendants.add(tool.name)
        }
    }

    return descendants
}

/**
 * Emits diagnostic hints for tool inputs that reference outputs from non-ancestor tools.
 *
 * Tool inputs can reference other tool outputs using template patterns like {{ToolName.output}}.
 * For these references to work at runtime, the referenced tool must be an ancestor (in the
 * dependency chain) of the tool using the reference. This validation emits hints (not errors)
 * to guide developers toward correct dependency declarations.
 *
 * @param toolDefinitions - Array of tool definitions to check
 * @param arg - The argument shape for diagnostics source location
 * @param diagnostics - Diagnostics for emitting hints
 */
function emitAncestorOnlyInputReferenceHints(
    toolDefinitions: ToolDefinition[],
    arg: ObjectShape,
    diagnostics: Context['diagnostics']
): void {
    const toolNames = new Set(toolDefinitions.map((t) => t.name))

    for (const tool of toolDefinitions) {
        if (!tool.inputs) {
            continue
        }

        const ancestors = getAncestors(tool.name, toolDefinitions)

        for (const input of tool.inputs) {
            // Match tool output reference pattern: {{ToolName.field}}
            const toolOutputMatch = input.value.match(/^\{\{([^.}]+)\.([^}]+)\}\}$/)
            if (toolOutputMatch) {
                const referencedTool = toolOutputMatch[1]
                if (referencedTool && toolNames.has(referencedTool) && !ancestors.has(referencedTool)) {
                    diagnostics.hint(
                        arg,
                        `Tool '${tool.name}' references output of '${referencedTool}' which is not an ancestor. ` +
                            `Add '${referencedTool}' to the depends array of '${tool.name}' to ensure correct execution order.`
                    )
                }
            }
        }
    }
}

/**
 * Emits diagnostic hints for Decision tools whose targets are not descendants.
 *
 * Decision node branches should target tools that come after the decision in the flow graph
 * (descendants). Targeting non-descendant tools may indicate a design issue. The '_end' target
 * is always valid as it represents the skill prompt termination.
 *
 * @param toolDefinitions - Array of tool definitions to check
 * @param arg - The argument shape for diagnostics source location
 * @param diagnostics - Diagnostics for emitting hints
 */
function emitDecisionDescendantTargetHints(
    toolDefinitions: ToolDefinition[],
    arg: ObjectShape,
    diagnostics: Context['diagnostics']
): void {
    const toolNames = new Set(toolDefinitions.map((t) => t.name))

    for (const tool of toolDefinitions) {
        if (tool.type !== 'decision' || !tool.targets) {
            continue
        }

        const descendants = getDescendants(tool.name, toolDefinitions)

        for (const target of tool.targets) {
            // '_end' is always valid
            if (target === '_end') {
                continue
            }

            // Target must exist as a tool
            if (!toolNames.has(target)) {
                continue
            } // Missing target is already caught by other validation

            // Target should be a descendant (comes after decision in the flow)
            if (!descendants.has(target)) {
                diagnostics.hint(
                    arg,
                    `Decision '${tool.name}' targets '${target}' which is not a descendant in the dependency graph. ` +
                        `Decision branches typically route to tools that come after the decision node.`
                )
            }
        }
    }
}

/**
 * Process all tools defined in a skill configuration
 *
 * Creates:
 * 1. Tool-specific records (resource_mapping, attribute_mapping, dependency edges)
 * 2. Parent skill resource_mapping (with tools, resource_capability == parent_capability)
 * 3. Default edges FROM each tool TO the parent skill resource_mapping
 *
 * @param toolsShape - The tools function shape from the skill config
 * @param capabilityRecord - The parent capability record
 * @param skillName - The skill name (used for edge internal_name)
 * @param arg - The argument shape for transformations
 * @param factory - The record factory
 * @param diagnostics - Diagnostics for error reporting
 * @param inputNames - Set of skill input names for collision validation
 * @returns Array of created records
 */
export async function processTools(
    toolsShape: Shape | undefined,
    capabilityRecord: BuildRecord,
    skillName: string,
    arg: ObjectShape,
    callExpression: CallExpressionShape,
    factory: Context['factory'],
    diagnostics: Context['diagnostics'],
    inputNames: Set<string>
): Promise<BuildRecord[]> {
    const records: BuildRecord[] = []

    // Map to track tool records for dependency resolution
    const toolRecordsMap = new Map<string, BuildRecord>()
    // Map to track each tool's _source Shape for use in leaf-tool edge creation
    const toolSourceMap = new Map<string, Shape>()

    // Track tools that are dependencies of other tools (to identify leaf tools later)
    const toolsWithDependents = new Set<string>()

    // Store Decision tools for edge creation after parent skill mapping is created
    let decisionToolsForEdges: ToolDefinition[] = []

    // Track leaf tool IDs for depends_on_list (computed if tools exist)
    const leafToolIds: Shape[] = []

    // Process tool definitions if tools are defined
    if (toolsShape && !toolsShape.isUndefined()) {
        // Check if tools is an arrow function using .if() pattern
        const arrowFunc = toolsShape.if(ArrowFunctionShape)
        if (!arrowFunc) {
            diagnostics.error(arg, 'tools must be an arrow function')
            return records
        }

        // Extract tool definitions from the arrow function
        const toolDefinitions = extractToolDefinitions(arrowFunc, diagnostics)

        // Post-processing: Add Decision dependencies to their targets
        // Decision targets should depend on the Decision for proper execution order.
        // This mirrors the post-processing in buildToolDefinitionsFromRecords (transform flow).
        // The depends property is NOT emitted in generated code for Decision targets (to avoid
        // redundancy with targets array), so we need to resolve it here during build.
        const toolByName = new Map<string, ToolDefinition>()
        for (const tool of toolDefinitions) {
            toolByName.set(tool.name, tool)
        }
        for (const tool of toolDefinitions) {
            if (tool.type === TOOL_TYPES.DECISION && tool.targets) {
                for (const targetName of tool.targets) {
                    if (targetName === '_end') {
                        continue
                    }
                    const targetTool = toolByName.get(targetName)
                    // Add Decision to target's depends if not already present
                    if (targetTool) {
                        if (!targetTool.depends) {
                            targetTool.depends = [tool.name]
                        } else if (!targetTool.depends.includes(tool.name)) {
                            targetTool.depends.push(tool.name)
                        }
                    }
                }
            }
        }

        // Collect tools that are dependencies of other tools (to identify leaf tools later)
        // A tool has dependents if another tool lists it in `depends` (upstream dependency).
        // Decision nodes are also marked as non-leaf because they are routing nodes — their
        // targets are the actual leaf tools that need edges back to the parent skill.
        // NOTE: Decision targets are NOT added here — they are still leaf tools whose
        // output flows to the skill.
        for (const tool of toolDefinitions) {
            if (tool.depends && tool.depends.length > 0) {
                // depends is an array of dependency names
                for (const dep of tool.depends) {
                    toolsWithDependents.add(dep)
                }
            }
            // Decision nodes are routing nodes. They are NOT leaf tools UNLESS they have '_end'
            // as a target (meaning they route directly to the parent skill via default branch).
            // If Decision has '_end' target, it IS a leaf tool for that path.
            if (tool.type === 'decision' && tool.name) {
                const hasEndTarget = tool.targets?.includes('_end')
                if (!hasEndTarget) {
                    // Only mark as non-leaf if it doesn't route to parent skill
                    toolsWithDependents.add(tool.name)
                }
            }
        }

        // Validate tool definitions
        const validationResult = validateToolDefinitions(toolDefinitions, inputNames)
        if (!validationResult.isValid) {
            diagnostics.error(arg, validationResult.errorMessage!)
            return records
        }

        // Emit non-blocking hints for dependency best practices
        emitAncestorOnlyInputReferenceHints(toolDefinitions, arg, diagnostics)
        emitDecisionDescendantTargetHints(toolDefinitions, arg, diagnostics)

        // Build toolSourceMap from definitions
        for (const tool of toolDefinitions) {
            if (tool.name && tool._source) {
                toolSourceMap.set(tool.name, tool._source!)
            }
        }

        // Collect Decision tool names to suppress warnings for Decision dependencies
        // (Decision edges are created separately by createDecisionEdges)
        const decisionToolNames = new Set<string>()
        for (const tool of toolDefinitions) {
            if (tool.type === TOOL_TYPES.DECISION) {
                decisionToolNames.add(tool.name)
            }
        }

        // Topologically sort tools so dependencies are processed before dependents.
        // After post-processing, Decision targets have depends set to the Decision name.
        // This ensures tools are processed in correct order for depends_on_list population.
        const sortedToolDefinitions = topologicalSortTools(toolDefinitions)

        // Process each tool definition
        for (const tool of sortedToolDefinitions) {
            let toolRecords: BuildRecord[] = []

            switch (tool.type) {
                case TOOL_TYPES.INLINE_SCRIPT:
                    toolRecords = await processInlineScriptTool(
                        tool,
                        capabilityRecord,
                        toolRecordsMap,
                        arg,
                        factory,
                        diagnostics,
                        decisionToolNames
                    )
                    break

                case TOOL_TYPES.SCRIPT:
                    toolRecords = await processScriptTool(
                        tool,
                        capabilityRecord,
                        toolRecordsMap,
                        arg,
                        factory,
                        diagnostics,
                        decisionToolNames
                    )
                    break

                case TOOL_TYPES.SUBFLOW:
                    toolRecords = await processSubflowTool(
                        tool,
                        capabilityRecord,
                        toolRecordsMap,
                        arg,
                        factory,
                        diagnostics,
                        decisionToolNames
                    )
                    break

                case TOOL_TYPES.WEB_SEARCH:
                    toolRecords = await processWebSearchTool(
                        tool,
                        capabilityRecord,
                        toolRecordsMap,
                        arg,
                        factory,
                        diagnostics,
                        decisionToolNames
                    )
                    break

                case TOOL_TYPES.FLOW_ACTION:
                    toolRecords = await processFlowActionTool(
                        tool,
                        capabilityRecord,
                        toolRecordsMap,
                        arg,
                        factory,
                        diagnostics,
                        decisionToolNames
                    )
                    break

                case TOOL_TYPES.SKILL:
                    toolRecords = await processSkillTool(
                        tool,
                        capabilityRecord,
                        toolRecordsMap,
                        arg,
                        factory,
                        diagnostics,
                        decisionToolNames
                    )
                    break

                case TOOL_TYPES.DECISION:
                    toolRecords = await processDecisionTool(
                        tool,
                        capabilityRecord,
                        toolRecordsMap,
                        arg,
                        factory,
                        diagnostics,
                        decisionToolNames
                    )
                    break
            }

            records.push(...toolRecords)
        }

        // Store Decision tools for edge creation after parent skill mapping is created
        decisionToolsForEdges = toolDefinitions.filter((t: ToolDefinition) => t.type === TOOL_TYPES.DECISION)

        // Leaf tools = tools that are NOT dependencies of other tools
        // Only leaf tools should be in skill's depends_on_list
        // EXCEPTION: Decision tools with '_end' target are leaf tools for that path (route to parent skill)

        // Collect Decision tools that have '_end' target (they route directly to parent skill)
        const decisionToolsWithEndTarget = new Set<string>()
        for (const decisionTool of decisionToolsForEdges) {
            if (decisionTool.targets?.includes('_end') && decisionTool.name) {
                decisionToolsWithEndTarget.add(decisionTool.name)
            }
        }

        for (const [toolName, record] of toolRecordsMap) {
            // Include if: not a dependent of other tools OR is a Decision with '_end' target
            if (!toolsWithDependents.has(toolName) || decisionToolsWithEndTarget.has(toolName)) {
                leafToolIds.push(record.getId())
            }
        }
    }

    // Always create the parent skill resource_mapping record (self-mapping: resource_capability == parent_capability)
    // This record is created even when no tools are defined, ensuring the skill's self-mapping always exists.
    // The SDK's coalesce keys (parent_capability + resource_capability) ensure no duplicate is created.
    // Convert Shape[] to comma-separated sys_id string for XML
    const dependsOnList = leafToolIds.length > 0 ? leafToolIds.map((id) => id.toString().getValue()).join(',') : ''

    const parentSkillResourceMapping = await factory.createRecord({
        source: {
            path: callExpression.getOriginalFilePath(),
            content: '',
        },
        table: SYS_ONE_EXTEND_RESOURCE_MAPPING,
        properties: arg.transform(({ $ }) => ({
            ...applyDefaults(SYS_ONE_EXTEND_RESOURCE_MAPPING, $),
            resource_name: $.val(skillName),
            parent_capability: $.val(capabilityRecord.getId()),
            resource_capability: $.val(capabilityRecord.getId()),
            applicability_script: $.val(RESOURCE_MAPPING_APPLICABILITY_SCRIPT),
            depends_on_list: $.val(dependsOnList),
            // Always set metadata to empty object so the platform regenerates the
            // skill diagram when the skill is opened in the UI. Without this, a
            // stale diagramInstanceId from a previous install prevents the platform
            // from picking up newly added or removed tools.
            metadata: $.val('{}'),
        })),
    })
    records.push(parentSkillResourceMapping)

    // Create Decision edges after all tools AND parent skill mapping are processed
    // Decision '_end' target points to parent skill mapping (prompt)
    if (decisionToolsForEdges.length > 0) {
        const decisionEdges = await createDecisionEdges(
            decisionToolsForEdges,
            toolRecordsMap,
            parentSkillResourceMapping,
            arg,
            factory,
            diagnostics
        )
        records.push(...decisionEdges)
    }

    // Create default edges FROM leaf tools TO the parent skill resource_mapping
    if (toolRecordsMap.size > 0) {
        // Only leaf tools (tools not depended on by other tools) have direct edges to the skill
        for (const [toolName, toolRecord] of toolRecordsMap) {
            // Skip non-leaf tools - they don't have edges directly to skill
            if (toolsWithDependents.has(toolName)) {
                continue
            }
            const defaultEdge = await factory.createRecord({
                source: toolSourceMap.get(toolName) ?? arg,
                table: SYS_ONE_EXTEND_RESOURCE_EDGE,
                properties: arg.transform(({ $ }) => ({
                    ...applyDefaults(SYS_ONE_EXTEND_RESOURCE_EDGE, $),
                    from_resource: $.val(toolRecord.getId()),
                    internal_name: $.val(`${toolName}__${skillName}`),
                    to_resource: $.val(parentSkillResourceMapping.getId()),
                })),
            })
            records.push(defaultEdge)
        }
    }

    return records
}
